import axios from 'axios'

import {
  ACCESS_TOKEN_KEY,
  APP_TOKEN_KEY,
  DEVICE_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  SAAS_TOKEN_KEY
} from '../constants'

export type StoredTokens = {
  access?: string
  refresh?: string
  device_token?: string
}

/**
 * Persist tokens to localStorage, writing each key only when a non-empty value
 * is provided. Never overwrites a stored token with an empty/undefined value —
 * critical under refresh-token rotation: a non-rotating refresh response carries
 * only `access` and must not wipe the stored refresh.
 */
export const persistTokens = (tokens: StoredTokens) => {
  if (tokens?.access) {
    globalThis.localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access)
  }
  if (tokens?.refresh) {
    globalThis.localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh)
  }
  if (tokens?.device_token) {
    globalThis.localStorage.setItem(DEVICE_TOKEN_KEY, tokens.device_token)
  }
}

export const setLocalStorageToken = (tokens: {
  access: string
  refresh: string
  device_token?: string
}) => {
  // Login / cookie path: response carries both tokens. Guarded so we never
  // clobber a stored token with an empty string (see persistTokens).
  persistTokens(tokens)
}

export const getLocalStorageTokens = () => {
  const access = window.localStorage.getItem(ACCESS_TOKEN_KEY)
  const refresh = window.localStorage.getItem(REFRESH_TOKEN_KEY)
  const saasToken = window.localStorage.getItem(SAAS_TOKEN_KEY)

  return {
    access,
    refresh,
    saasToken
  }
}

export const clearLocalStorage = () => {
  window.localStorage.removeItem(REFRESH_TOKEN_KEY)
  window.localStorage.removeItem(ACCESS_TOKEN_KEY)
  window.localStorage.removeItem(SAAS_TOKEN_KEY)

  Object.keys(window.localStorage)
    .filter((v) => v.startsWith(APP_TOKEN_KEY))
    .forEach((key) => window.localStorage.removeItem(key))
}

export const setAppToken = (app: string, token: string) => {
  window.localStorage.setItem(`${APP_TOKEN_KEY}${app}`, token)
}

export const getAppToken = (app: string) => {
  return window.localStorage.getItem(`${APP_TOKEN_KEY}${app}`)
}

export const getTokenFromUrl = () => {
  return window.location.pathname
    .split('/')
    .filter((i) => i !== '')
    .slice(2, 3)
    .join()
}

/**
 * getBaseUrl is used to get the base url from window
 * location pathname for routes to work properly
 */
export const getBaseUrl = () => {
  const path = window.location.pathname
    .split('/')
    .filter((i) => i !== '')
    .slice(0, 3)
    .join('/')

  return `/${path}`
}

/**
 * SSO base refresh URL, injected by the app layer (where Vite's
 * `import.meta.env.VITE_APP_SSO_API` is available) via
 * `getConfigInterceptors({ ssoRefreshUrl })`. Keeping it injected leaves this
 * shared lib free of build-tool/env coupling and keeps it unit-testable.
 */
let ssoRefreshUrl = ''

export const setSsoRefreshUrl = (url: string) => {
  ssoRefreshUrl = url
}

export const getSsoRefreshUrl = () => ssoRefreshUrl

/**
 * Exchange the refresh token for a fresh access token (and, under SSO refresh
 * rotation, a fresh refresh token). Uses a bare axios call so it never re-enters
 * the response interceptor. Persists whatever tokens come back (guarded) and
 * returns them. Throws on failure so a single-flight caller can react once.
 */
export const refresh = async (refreshToken: string): Promise<StoredTokens> => {
  if (!ssoRefreshUrl) {
    console.error(
      '[auth] SSO refresh URL is not configured; pass { ssoRefreshUrl } to getConfigInterceptors at axios setup.'
    )
    throw new Error('SSO refresh URL is not configured')
  }

  const { data: tokens } = await axios.post<StoredTokens>(ssoRefreshUrl, {
    refresh: refreshToken
  })

  persistTokens(tokens)
  console.debug('[auth] token refreshed', { rotated: Boolean(tokens?.refresh) })

  return tokens
}
